import * as JSZip from 'jszip';
import { triggerBlobDownload } from './fileSaver';
import { extractMediaUrls } from './outputNormalize';
import { TaskStatus, type Task } from '../hooks/useTaskManager';
import type { TaskSelfRecord } from '../store/taskSelfStore';
import { useAppStore } from '../store/appStore';

const DB_NAME = 'iarts_task_media_v1';
const DB_VERSION = 2; // 升级版本以添加 inline 媒体存储
const STORE_NAME = 'media';
const INLINE_STORE_NAME = 'inline_media'; // 存储 inline base64 数据

interface TaskMediaRecord {
  url: string;
  blob: Blob;
  mimeType?: string;
  size: number;
  createdAt: number;
  updatedAt: number;
}

// ============ Inline Base64 缓存类型 ============

/** 内联媒体缓存记录（用于存储 inline base64 数据） */
interface TaskInlineMediaRecord {
  /** 缓存 ID，格式: inline_${taskId}_${index} */
  cacheId: string;
  /** 二进制数据 */
  blob: Blob;
  /** MIME 类型 */
  mimeType: string;
  /** 文件大小 */
  size: number;
  /** 所属任务 ID */
  taskId: string;
  /** 缓存时间 */
  cachedAt: number;
}

/** 媒体缓存引用（存储在 localStorage 中替代原始 base64） */
export interface TaskMediaCacheRef {
  __mediaCacheRef__: {
    cacheId: string;
    mimeType: string;
    sizeBytes?: number;
  };
}

const hasIndexedDbSupport = (): boolean =>
  typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined';

const isBrowser = (): boolean => typeof window !== 'undefined';

const getMediaCacheConfig = () => {
  try {
    const state = useAppStore.getState();
    return {
      ttl: state.mediaCacheTTL,
      maxSize: state.mediaCacheMaxSize
    };
  } catch {
    return {
      ttl: 30 * 24 * 60 * 60 * 1000,
      maxSize: 1024 * 1024 * 1024
    };
  }
};

const isCacheableUrl = (value?: string | null): boolean => {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith('blob:') || trimmed.startsWith('data:')) {
    // blob / data URL 由浏览器本地生成，无需进入缓存流程
    return false;
  }
  if (!/^https?:\/\//i.test(trimmed)) {
    return false;
  }

  // 避免对对象存储签名 URL 触发跨域请求，这类链接通常是短时授权的临时访问地址，不适合做长期缓存。
  // 覆盖常见的对象存储服务：
  // - 火山引擎 TOS: X-Tos-Algorithm / X-Tos-Credential / X-Tos-Signature
  // - AWS S3: X-Amz-Signature / AWSAccessKeyId
  // - 阿里云 OSS: OSSAccessKeyId / Signature
  // - 腾讯云 COS: q-sign-algorithm / q-ak / q-signature
  try {
    const url = new URL(trimmed);
    const params = url.searchParams;
    const objectStorageSignatureParams = [
      // 火山引擎 TOS
      'X-Tos-Algorithm',
      'X-Tos-Credential',
      'X-Tos-Signature',
      // AWS S3
      'X-Amz-Signature',
      'AWSAccessKeyId',
      // 阿里云 OSS
      'OSSAccessKeyId',
      'Signature',
      // 腾讯云 COS
      'q-sign-algorithm',
      'q-ak',
      'q-signature'
    ];

    for (const key of objectStorageSignatureParams) {
      if (params.has(key)) {
        return false;
      }
    }
  } catch {
    // URL 解析失败时保持原有行为，默认认为可缓存
  }

  return true;
};

let dbPromise: Promise<IDBDatabase | null> | null = null;

const openDb = async (): Promise<IDBDatabase | null> => {
  if (!hasIndexedDbSupport()) return null;
  if (!dbPromise) {
    dbPromise = new Promise<IDBDatabase | null>((resolve, reject) => {
      try {
        const request = window.indexedDB.open(DB_NAME, DB_VERSION);
        request.onupgradeneeded = (event) => {
          const db = request.result;
          // 创建或保留原有的 URL 媒体存储
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            db.createObjectStore(STORE_NAME, { keyPath: 'url' });
          }
          // 创建 inline 媒体存储（版本 2 新增）
          if (!db.objectStoreNames.contains(INLINE_STORE_NAME)) {
            const inlineStore = db.createObjectStore(INLINE_STORE_NAME, { keyPath: 'cacheId' });
            inlineStore.createIndex('taskId', 'taskId', { unique: false });
            inlineStore.createIndex('cachedAt', 'cachedAt', { unique: false });
          }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error('Failed to open task media database'));
        request.onblocked = () => reject(new Error('Task media database open request blocked'));
      } catch (error) {
        reject(error);
      }
    });
  }
  try {
    return await dbPromise;
  } catch {
    dbPromise = null;
    return null;
  }
};

const getMediaRecordInternal = async (url: string): Promise<TaskMediaRecord | null> => {
  const db = await openDb();
  if (!db) return null;
  return new Promise<TaskMediaRecord | null>((resolve) => {
    try {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(url) as IDBRequest<TaskMediaRecord | undefined>;
      req.onsuccess = () => {
        const record = req.result ?? null;
        resolve(record);
      };
      req.onerror = () => {
        resolve(null);
      };
      tx.onabort = () => {
        resolve(null);
      };
    } catch {
      resolve(null);
    }
  });
};

const deleteMediaRecordInternal = async (url: string): Promise<void> => {
  const db = await openDb();
  if (!db) return;

  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.delete(url);

      const finish = () => {
        tx.oncomplete = null;
        tx.onerror = null;
        tx.onabort = null;
        resolve();
      };

      request.onerror = () => {
        // 删除失败不阻断整体流程，但记录警告方便排查
         
        console.warn('[taskMediaCache] Failed to delete media record', url, request.error);
      };

      tx.oncomplete = () => {
        finish();
      };
      tx.onerror = () => {
         
        console.warn('[taskMediaCache] Transaction error while deleting media record', url, tx.error);
        finish();
      };
      tx.onabort = () => {
         
        console.warn('[taskMediaCache] Transaction aborted while deleting media record', url, tx.error);
        finish();
      };
    } catch (error) {
       
      console.warn('[taskMediaCache] Exception while deleting media record', url, error);
      resolve();
    }
  });
};

const saveMediaRecordInternal = async (record: TaskMediaRecord): Promise<void> => {
  const db = await openDb();
  if (!db) return;

  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.put(record);

      const finish = () => {
        tx.oncomplete = null;
        tx.onerror = null;
        tx.onabort = null;
        resolve();
      };

      request.onerror = () => {
        // 写入失败时仅记录日志，不中断调用方逻辑，保持幂等
         
        console.warn('[taskMediaCache] Failed to save media record', record.url, request.error);
      };

      tx.oncomplete = () => {
        finish();
      };
      tx.onerror = () => {
         
        console.warn('[taskMediaCache] Transaction error while saving media record', record.url, tx.error);
        finish();
      };
      tx.onabort = () => {
         
        console.warn('[taskMediaCache] Transaction aborted while saving media record', record.url, tx.error);
        finish();
      };
    } catch (error) {
       
      console.warn('[taskMediaCache] Exception while saving media record', record.url, error);
      resolve();
    }
  });
};

const inFlightFetches = new Map<string, Promise<void>>();
// 用于在“手动全量清理”后阻止旧的 in-flight 预缓存写回
let mediaCacheClearVersion = 0;

const ensureMediaCachedForUrl = async (url: string): Promise<void> => {
  if (!isBrowser() || !isCacheableUrl(url)) return;

  const clearVersionAtStart = mediaCacheClearVersion;
  const { ttl } = getMediaCacheConfig();

  const existingPromise = inFlightFetches.get(url);
  if (existingPromise) {
    await existingPromise;
    return;
  }

  const fetchPromise = (async () => {
    try {
      const existing = await getMediaRecordInternal(url);
      const now = Date.now();
      if (existing) {
        if (now - existing.createdAt <= ttl) {
          return;
        }
        await deleteMediaRecordInternal(url);
      }

      const response = await fetch(url);
      if (!response.ok) {
        return;
      }
      const blob = await response.blob();
      const mimeType = response.headers.get('content-type') ?? blob.type ?? undefined;
      const record: TaskMediaRecord = {
        url,
        blob,
        mimeType,
        size: blob.size,
        createdAt: now,
        updatedAt: now,
      };
      // 若在下载过程中用户触发了全量清理，则放弃写回
      if (clearVersionAtStart !== mediaCacheClearVersion) {
        return;
      }
      await saveMediaRecordInternal(record);
    } catch {
      // 忽略缓存失败，避免阻断其它任务
    }
  })();

  inFlightFetches.set(url, fetchPromise);
  try {
    await fetchPromise;
  } finally {
    inFlightFetches.delete(url);
  }
};

const getAllMediaRecordsInternal = async (): Promise<TaskMediaRecord[]> => {
  const db = await openDb();
  if (!db) return [];
  return new Promise<TaskMediaRecord[]>((resolve) => {
    try {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.getAll() as IDBRequest<TaskMediaRecord[]>;
      req.onsuccess = () => resolve(req.result ?? []);
      req.onerror = () => {
        resolve([]);
      };
      tx.onabort = () => {
        resolve([]);
      };
    } catch {
      resolve([]);
    }
  });
};

const clearAllMediaRecordsInternal = async (): Promise<void> => {
  const db = await openDb();
  if (!db) return;

  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.clear();

      const finish = () => {
        tx.oncomplete = null;
        tx.onerror = null;
        tx.onabort = null;
        resolve();
      };

      tx.oncomplete = finish;
      tx.onerror = () => {
        console.warn('[taskMediaCache] Transaction error while clearing media cache', tx.error);
        finish();
      };
      tx.onabort = () => {
        console.warn('[taskMediaCache] Transaction aborted while clearing media cache', tx.error);
        finish();
      };
    } catch (error) {
      console.warn('[taskMediaCache] Exception while clearing media cache', error);
      resolve();
    }
  });
};

export const cleanupOldCache = async (): Promise<void> => {
  if (!isBrowser()) return;
  const { ttl, maxSize } = getMediaCacheConfig();
  const records = await getAllMediaRecordsInternal();
  if (!records.length) return;
  const now = Date.now();
  let totalSize = 0;
  const validRecords: TaskMediaRecord[] = [];
  const expiredUrls: string[] = [];
  for (const record of records) {
    if (now - record.createdAt > ttl) {
      expiredUrls.push(record.url);
      continue;
    }
    totalSize += record.size;
    validRecords.push(record);
  }
  validRecords.sort((a, b) => a.createdAt - b.createdAt);
  let index = 0;
  while (totalSize > maxSize && index < validRecords.length) {
    const record = validRecords[index++];
    totalSize -= record.size;
    expiredUrls.push(record.url);
  }
  await Promise.all(expiredUrls.map((url) => deleteMediaRecordInternal(url)));
};

/**
 * 手动全量清空异步任务媒体缓存（IndexedDB media store）。
 * 注意：不会清理 inline_media（同步任务 base64 缓存），以避免影响同步任务列表展示。
 */
export const clearAllMediaCache = async (): Promise<void> => {
  if (!isBrowser()) return;
  mediaCacheClearVersion += 1;
  await clearAllMediaRecordsInternal();
};

export const getMediaCacheStats = async (): Promise<{ totalSize: number; count: number }> => {
  if (!isBrowser()) return { totalSize: 0, count: 0 };
  const records = await getAllMediaRecordsInternal();
  let totalSize = 0;
  for (const record of records) {
    totalSize += record.size;
  }
  return { totalSize, count: records.length };
};

export const prefetchTaskMediaFromRecords = async (records: TaskSelfRecord[]): Promise<void> => {
  if (!isBrowser()) return;
  const urls = new Set<string>();
  for (const record of records) {
    const task: Task = record.task;
    if (!task || task.status !== TaskStatus.SUCCESS) continue;
    try {
      const extracted = extractMediaUrls(task.result);
      for (const url of extracted.images) {
        if (isCacheableUrl(url)) {
          urls.add(url);
        }
      }
      for (const url of extracted.videos) {
        if (isCacheableUrl(url)) {
          urls.add(url);
        }
      }
    } catch {
      // ignore
    }
  }
  if (!urls.size) return;
  await Promise.all(Array.from(urls).map((url) => ensureMediaCachedForUrl(url)));
  await cleanupOldCache();
};

export const getTaskMediaObjectUrl = async (url: string): Promise<string | null> => {
  if (!isBrowser() || !isCacheableUrl(url)) return null;
  const record = await getMediaRecordInternal(url);
  if (!record) return null;
  const now = Date.now();
  const { ttl } = getMediaCacheConfig();
  if (now - record.createdAt > ttl) {
    await deleteMediaRecordInternal(url);
    return null;
  }
  try {
    return URL.createObjectURL(record.blob);
  } catch {
    return null;
  }
};

const sanitizeFilename = (value: string): string => {
  const fallback = 'media';
  if (!value) return fallback;
  return value
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '_')
    .replace(/\s+/g, '_')
    .replace(/\.+$/, '')
    .slice(0, 255) || fallback;
};

const formatTimestampForFilename = (date: Date): string => {
  const pad = (num: number): string => num.toString().padStart(2, '0');
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}-${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
};

export const downloadAllMediaCache = async (): Promise<void> => {
  if (!isBrowser()) {
    throw new Error('Media cache download is only available in browser environment');
  }
  try {
    const records = await getAllMediaRecordsInternal();
    if (!records.length) {
      throw new Error('No media cache to download');
    }

    const zip = new JSZip.default();
    const usedNames = new Set<string>();
    records.forEach((record, index) => {
      const filenameFromUrl = (() => {
        try {
          const parsed = new URL(record.url);
          const pathSegments = parsed.pathname.split('/').filter(Boolean);
          return pathSegments.pop() ?? '';
        } catch {
          const segments = record.url.split('/').filter(Boolean);
          return segments.pop() ?? '';
        }
      })();

      let filename = sanitizeFilename(filenameFromUrl || `media-${index + 1}`);
      while (usedNames.has(filename)) {
        filename = sanitizeFilename(`${filename}-${index + 1}`);
      }
      usedNames.add(filename);
      zip.file(filename, record.blob);
    });

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const timestamp = formatTimestampForFilename(new Date());
    const archiveName = `task-media-cache-${timestamp}.zip`;
    await triggerBlobDownload(zipBlob, archiveName);
  } catch (error) {
    throw error;
  }
};

// 暴露必要的内部逻辑供单元测试验证事务语义
export const __taskMediaCacheInternals = {
  openDb,
  deleteMediaRecordInternal,
  saveMediaRecordInternal,
  getAllMediaRecordsInternal,
  getMediaRecordInternal,
  clearAllMediaRecordsInternal,
};

// ============ Inline Base64 缓存功能 ============

/** base64 数据的最小长度阈值（小于此值不缓存到 IndexedDB） */
const MIN_BASE64_LENGTH = 10_000;

/** 检测 base64 字符串的正则（至少 100 个有效字符） */
const BASE64_PATTERN = /^[A-Za-z0-9+/=]{100,}$/;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

const isLikelyBase64 = (value: string): boolean => {
  if (value.length < MIN_BASE64_LENGTH) return false;
  // 检查前 200 个字符是否符合 base64 模式
  const sample = value.slice(0, 200).replace(/\s+/g, '');
  return BASE64_PATTERN.test(sample);
};

/**
 * 将 base64 字符串转换为 Blob
 */
const base64ToBlob = (base64: string, mimeType: string): Blob => {
  // 移除可能的 data URL 前缀
  const cleanBase64 = base64.includes(',') ? base64.split(',')[1] : base64;
  const sanitized = cleanBase64.replace(/\s+/g, '');

  if (typeof Buffer !== 'undefined') {
    return new Blob([Buffer.from(sanitized, 'base64')], { type: mimeType });
  }

  if (typeof atob === 'function') {
    const binary = atob(sanitized);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: mimeType });
  }

  throw new Error('当前环境不支持 base64 转换');
};

/**
 * 将 Blob 转换为 base64 字符串
 */
const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // 移除 data URL 前缀，只返回纯 base64
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Blob 转 base64 失败'));
    reader.readAsDataURL(blob);
  });

// ============ Inline 媒体 IndexedDB 操作 ============

const saveInlineMediaRecord = async (record: TaskInlineMediaRecord): Promise<void> => {
  const db = await openDb();
  if (!db) return;

  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(INLINE_STORE_NAME, 'readwrite');
      const store = tx.objectStore(INLINE_STORE_NAME);
      const request = store.put(record);

      const finish = () => {
        tx.oncomplete = null;
        tx.onerror = null;
        tx.onabort = null;
        resolve();
      };

      request.onerror = () => {
        console.warn('[taskMediaCache] Failed to save inline media record', record.cacheId, request.error);
      };

      tx.oncomplete = finish;
      tx.onerror = () => {
        console.warn('[taskMediaCache] Transaction error while saving inline media', record.cacheId, tx.error);
        finish();
      };
      tx.onabort = () => {
        console.warn('[taskMediaCache] Transaction aborted while saving inline media', record.cacheId, tx.error);
        finish();
      };
    } catch (error) {
      console.warn('[taskMediaCache] Exception while saving inline media record', record.cacheId, error);
      resolve();
    }
  });
};

const getInlineMediaRecord = async (cacheId: string): Promise<TaskInlineMediaRecord | null> => {
  const db = await openDb();
  if (!db) return null;

  return new Promise<TaskInlineMediaRecord | null>((resolve) => {
    try {
      const tx = db.transaction(INLINE_STORE_NAME, 'readonly');
      const store = tx.objectStore(INLINE_STORE_NAME);
      const req = store.get(cacheId) as IDBRequest<TaskInlineMediaRecord | undefined>;
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => resolve(null);
      tx.onabort = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
};

const deleteInlineMediaByTaskId = async (taskId: string): Promise<void> => {
  const db = await openDb();
  if (!db) return;

  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(INLINE_STORE_NAME, 'readwrite');
      const store = tx.objectStore(INLINE_STORE_NAME);
      const index = store.index('taskId');
      const request = index.getAllKeys(IDBKeyRange.only(taskId));

      request.onsuccess = () => {
        const keys = request.result ?? [];
        for (const key of keys) {
          store.delete(key);
        }
      };

      const finish = () => {
        tx.oncomplete = null;
        tx.onerror = null;
        tx.onabort = null;
        resolve();
      };

      tx.oncomplete = finish;
      tx.onerror = () => {
        console.warn('[taskMediaCache] Transaction error while deleting inline media by taskId', taskId, tx.error);
        finish();
      };
      tx.onabort = () => {
        console.warn('[taskMediaCache] Transaction aborted while deleting inline media by taskId', taskId, tx.error);
        finish();
      };
    } catch (error) {
      console.warn('[taskMediaCache] Exception while deleting inline media by taskId', taskId, error);
      resolve();
    }
  });
};

const clearAllInlineMedia = async (): Promise<void> => {
  const db = await openDb();
  if (!db) return;

  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(INLINE_STORE_NAME, 'readwrite');
      const store = tx.objectStore(INLINE_STORE_NAME);
      store.clear();

      const finish = () => {
        tx.oncomplete = null;
        tx.onerror = null;
        tx.onabort = null;
        resolve();
      };

      tx.oncomplete = finish;
      tx.onerror = () => {
        console.warn('[taskMediaCache] Transaction error while clearing inline media', tx.error);
        finish();
      };
      tx.onabort = () => {
        console.warn('[taskMediaCache] Transaction aborted while clearing inline media', tx.error);
        finish();
      };
    } catch (error) {
      console.warn('[taskMediaCache] Exception while clearing inline media', error);
      resolve();
    }
  });
};

// ============ 提取逻辑 ============

interface ExtractionContext {
  taskId: string;
  cacheIds: string[];
  index: number;
}

/**
 * 缓存单个 base64 数据到 IndexedDB
 */
const cacheBase64Data = async (
  base64: string,
  mimeType: string,
  context: ExtractionContext
): Promise<TaskMediaCacheRef | null> => {
  try {
    const cacheId = `inline_${context.taskId}_${context.index}`;
    context.index++;

    const blob = base64ToBlob(base64, mimeType);
    const sizeBytes = blob.size;

    const record: TaskInlineMediaRecord = {
      cacheId,
      blob,
      mimeType,
      size: sizeBytes,
      taskId: context.taskId,
      cachedAt: Date.now(),
    };

    await saveInlineMediaRecord(record);
    context.cacheIds.push(cacheId);

    return {
      __mediaCacheRef__: {
        cacheId,
        mimeType,
        sizeBytes,
      },
    };
  } catch (error) {
    console.warn('[taskMediaCache] 缓存 base64 数据失败', error);
    return null;
  }
};

/**
 * 递归遍历对象，提取并缓存 inline base64 数据
 * 返回替换后的对象（原对象不修改）
 */
const extractRecursive = async (
  value: unknown,
  context: ExtractionContext,
  depth = 0
): Promise<unknown> => {
  // 防止无限递归
  if (depth > 20) return value;

  // 处理数组
  if (Array.isArray(value)) {
    const results = await Promise.all(
      value.map((item) => extractRecursive(item, context, depth + 1))
    );
    return results;
  }

  // 处理对象
  if (isRecord(value)) {
    // 检测 Gemini 格式: { inlineData: { data, mimeType } }
    const inlineData = value.inlineData ?? value.inline_data;
    if (isRecord(inlineData)) {
      const data = inlineData.data ?? inlineData.Data;
      const mimeType =
        (inlineData.mimeType as string) ??
        (inlineData.mime_type as string) ??
        'image/png';

      if (isNonEmptyString(data) && isLikelyBase64(data)) {
        const cacheRef = await cacheBase64Data(data, mimeType, context);
        if (cacheRef) {
          // 保留其他字段，只替换 data
          const key = 'inlineData' in value ? 'inlineData' : 'inline_data';
          const mimeKey = 'mimeType' in (inlineData as Record<string, unknown>) ? 'mimeType' : 'mime_type';
          return {
            ...value,
            [key]: {
              ...inlineData,
              data: undefined, // 移除原始 data
              [mimeKey]: mimeType,
              __mediaCacheRef__: cacheRef.__mediaCacheRef__,
            },
          };
        }
      }
    }

    // 检测 OpenAI 格式: { b64_json: "..." }
    const b64json = value.b64_json ?? value.b64Json;
    if (isNonEmptyString(b64json) && isLikelyBase64(b64json)) {
      const cacheRef = await cacheBase64Data(b64json, 'image/png', context);
      if (cacheRef) {
        const key = 'b64_json' in value ? 'b64_json' : 'b64Json';
        return {
          ...value,
          [key]: undefined,
          __mediaCacheRef__: cacheRef.__mediaCacheRef__,
        };
      }
    }

    // 递归处理子字段
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      result[key] = await extractRecursive(val, context, depth + 1);
    }
    return result;
  }

  return value;
};

// ============ 恢复逻辑 ============

/**
 * 从 IndexedDB 恢复单个缓存引用
 */
const restoreCacheRef = async (
  ref: TaskMediaCacheRef['__mediaCacheRef__']
): Promise<{ base64: string; mimeType: string } | null> => {
  try {
    const record = await getInlineMediaRecord(ref.cacheId);
    if (!record?.blob) {
      console.warn('[taskMediaCache] 缓存条目不存在', ref.cacheId);
      return null;
    }

    const base64 = await blobToBase64(record.blob);
    return {
      base64,
      mimeType: record.mimeType || ref.mimeType,
    };
  } catch (error) {
    console.warn('[taskMediaCache] 恢复缓存失败', ref.cacheId, error);
    return null;
  }
};

/**
 * 递归遍历对象，从 IndexedDB 恢复 base64 数据
 */
const restoreRecursive = async (value: unknown, depth = 0): Promise<unknown> => {
  if (depth > 20) return value;

  if (Array.isArray(value)) {
    const results = await Promise.all(
      value.map((item) => restoreRecursive(item, depth + 1))
    );
    return results;
  }

  if (isRecord(value)) {
    // 检测顶层的 cacheRef 并恢复（OpenAI 格式）
    const mediaCacheRef = value.__mediaCacheRef__;
    if (isRecord(mediaCacheRef) && isNonEmptyString(mediaCacheRef.cacheId)) {
      const restored = await restoreCacheRef(mediaCacheRef as TaskMediaCacheRef['__mediaCacheRef__']);
      if (restored) {
        const result = { ...value };
        delete result.__mediaCacheRef__;
        // OpenAI 格式：恢复 b64_json 字段
        result.b64_json = restored.base64;
        return result;
      }
      // 恢复失败，保留 cacheRef 标记
      return value;
    }

    // 检测嵌套的 inlineData 中的 cacheRef（Gemini 格式）
    const inlineData = value.inlineData ?? value.inline_data;
    if (isRecord(inlineData) && isRecord(inlineData.__mediaCacheRef__)) {
      const ref = inlineData.__mediaCacheRef__ as TaskMediaCacheRef['__mediaCacheRef__'];
      const restored = await restoreCacheRef(ref);
      if (restored) {
        const key = 'inlineData' in value ? 'inlineData' : 'inline_data';
        const restoredInlineData = { ...inlineData };
        delete restoredInlineData.__mediaCacheRef__;
        restoredInlineData.data = restored.base64;
        return {
          ...value,
          [key]: restoredInlineData,
        };
      }
    }

    // 递归处理子字段
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      if (key === '__mediaCacheRefs__') {
        // 跳过顶层的 cacheIds 列表，恢复后不再需要
        continue;
      }
      result[key] = await restoreRecursive(val, depth + 1);
    }
    return result;
  }

  return value;
};

// ============ 导出的 Inline 媒体缓存函数 ============

/**
 * 提取并缓存任务结果中的 inline base64 数据
 * @param taskId 任务 ID
 * @param result 原始任务结果
 * @returns 替换后的结果对象（base64 被替换为 cacheRef）
 */
export const extractAndCacheInlineMedia = async (
  taskId: string,
  result: unknown
): Promise<unknown> => {
  if (!result || typeof result !== 'object') {
    return result;
  }

  // 先清理该任务之前可能存在的缓存
  await deleteInlineMediaByTaskId(taskId);

  const context: ExtractionContext = {
    taskId,
    cacheIds: [],
    index: 0,
  };

  try {
    const extracted = await extractRecursive(result, context);

    // 如果有缓存的媒体，添加 cacheIds 列表到顶层
    if (context.cacheIds.length > 0 && isRecord(extracted)) {
      return {
        ...extracted,
        __mediaCacheRefs__: context.cacheIds,
      };
    }

    return extracted;
  } catch (error) {
    console.warn('[taskMediaCache] 提取媒体数据失败', error);
    return result;
  }
};

/**
 * 从 IndexedDB 恢复任务结果中的 base64 数据
 * @param result 带有 cacheRef 的任务结果
 * @returns 恢复后的原始结果对象
 */
export const restoreInlineMedia = async (result: unknown): Promise<unknown> => {
  if (!result || typeof result !== 'object') {
    return result;
  }

  // 快速检查是否有缓存引用
  if (isRecord(result) && !result.__mediaCacheRefs__) {
    // 没有媒体缓存引用，直接返回
    return result;
  }

  try {
    return await restoreRecursive(result);
  } catch (error) {
    console.warn('[taskMediaCache] 恢复媒体数据失败', error);
    return result;
  }
};

/**
 * 清理指定任务的 inline 媒体缓存
 * @param taskId 任务 ID
 */
export const cleanupTaskInlineMediaCache = async (taskId: string): Promise<void> => {
  await deleteInlineMediaByTaskId(taskId);
};

/**
 * 清理所有 inline 媒体缓存
 */
export const clearAllInlineMediaCache = async (): Promise<void> => {
  await clearAllInlineMedia();
};

/**
 * 检查结果是否包含媒体缓存引用
 */
export const hasMediaCacheRefs = (result: unknown): boolean => {
  if (!isRecord(result)) return false;
  return Array.isArray(result.__mediaCacheRefs__) && result.__mediaCacheRefs__.length > 0;
};
